import type {
  SectionConfig,
  TemplateSection,
} from "../../types/reportTemplate";
import { isEmptyHtml } from "../../utils/helpers";
import {
  buildRichTextReportFragments,
  type RichTextReportFragment,
} from "./addendumReportFragments";

type SpecimenDescriptionSectionType =
  | "microscopic_description"
  | "gross_description";
type SpecimenDescriptionSection = TemplateSection & {
  type: SpecimenDescriptionSectionType;
};

interface SpecimenDescriptionFragmentInput {
  section: TemplateSection;
  sectionIndex: number;
  specimenContents: string[];
  combinedContent: string;
  fragmentedDescriptionKeys: ReadonlySet<string>;
  narrativeFragmentsByContent?: ReadonlyMap<string, RichTextReportFragment[]>;
}

export interface ReportNarrativeUnit {
  contentHtml: string;
  key: string;
  config: SectionConfig;
  showLabel: boolean;
}

interface ReportNarrativeSectionsInput {
  section: TemplateSection;
  sectionIndex: number;
  units: ReportNarrativeUnit[];
  fragmentedNarrativeKeys: ReadonlySet<string>;
  includeEmptyUnits?: boolean;
  narrativeFragmentsByContent?: ReadonlyMap<string, RichTextReportFragment[]>;
}

interface NarrativeFragmentsInput {
  section: TemplateSection;
  unit: ReportNarrativeUnit;
  fragments: RichTextReportFragment[];
  narrativeKey: string;
  showHeading: boolean;
}

interface WholeNarrativeSectionInput {
  section: TemplateSection;
  unit: ReportNarrativeUnit;
  narrativeKey: string;
  showHeading: boolean;
  canFragment: boolean;
}

export function isSpecimenDescriptionSection(
  section: TemplateSection
): section is SpecimenDescriptionSection {
  return (
    section.type === "microscopic_description" ||
    section.type === "gross_description"
  );
}

export function buildSpecimenDescriptionReportSections({
  section,
  sectionIndex,
  specimenContents,
  combinedContent,
  fragmentedDescriptionKeys,
  narrativeFragmentsByContent,
}: SpecimenDescriptionFragmentInput): TemplateSection[] {
  if (!isSpecimenDescriptionSection(section)) return [section];
  const units = buildDescriptionUnits(
    section,
    specimenContents,
    combinedContent
  );
  if (!units) return [section];
  return buildReportNarrativeSections({
    section,
    sectionIndex,
    units,
    fragmentedNarrativeKeys: fragmentedDescriptionKeys,
    narrativeFragmentsByContent,
  });
}

export function buildReportNarrativeSections({
  section,
  sectionIndex,
  units,
  fragmentedNarrativeKeys,
  includeEmptyUnits = false,
  narrativeFragmentsByContent,
}: ReportNarrativeSectionsInput): TemplateSection[] {
  const visibleUnits = includeEmptyUnits
    ? units
    : units.filter(({ contentHtml }) => !isEmptyHtml(contentHtml));
  return visibleUnits.flatMap((unit, index) => {
    const narrativeKey = buildNarrativeKey(sectionIndex, unit.key);
    const fragments =
      narrativeFragmentsByContent?.get(unit.contentHtml) ??
      buildRichTextReportFragments(unit.contentHtml, {
        splitAtEveryBreakpoint: true,
      });
    return fragmentedNarrativeKeys.has(narrativeKey)
      ? buildNarrativeFragments({
          section,
          unit,
          fragments,
          narrativeKey,
          showHeading: index === 0,
        })
      : [
          buildWholeNarrativeSection({
            section,
            unit,
            narrativeKey,
            showHeading: index === 0,
            canFragment: fragments.length > 1,
          }),
        ];
  });
}

function buildDescriptionUnits(
  section: SpecimenDescriptionSection,
  specimenContents: string[],
  combinedContent: string
): ReportNarrativeUnit[] | null {
  if (section.config?.combined) {
    return [
      {
        contentHtml: combinedContent,
        key: "combined",
        config: { combined: true },
        showLabel: false,
      },
    ];
  }
  if (!section.config?.perSpecimen) return null;
  return specimenContents.map((contentHtml, specimenIndex) => ({
    contentHtml,
    key: String(specimenIndex),
    config: { specimenIndex },
    showLabel: true,
  }));
}

function buildNarrativeFragments({
  section,
  unit,
  fragments,
  narrativeKey,
  showHeading,
}: NarrativeFragmentsInput): TemplateSection[] {
  const firstContentIndex = fragments.findIndex(
    (fragment) => !isEmptyHtml(fragment.contentHtml)
  );
  return fragments.map((fragment, index) => ({
    ...section,
    config: {
      ...section.config,
      ...unit.config,
      showHeading: showHeading && index === firstContentIndex,
      reportNarrativeKey: narrativeKey,
      reportNarrativeFragmentIndex: index,
      reportNarrativeFragmentContentHtml: fragment.contentHtml,
      reportNarrativeFragmentShowLabel:
        unit.showLabel && index === firstContentIndex,
    },
  }));
}

function buildWholeNarrativeSection({
  section,
  unit,
  narrativeKey,
  showHeading,
  canFragment,
}: WholeNarrativeSectionInput): TemplateSection {
  return {
    ...section,
    config: {
      ...section.config,
      ...unit.config,
      showHeading,
      reportNarrativeKey: narrativeKey,
      reportNarrativeFragmentShowLabel: unit.showLabel,
      reportNarrativeCanFragment: canFragment,
    },
  };
}

function buildNarrativeKey(sectionIndex: number, unitKey: string): string {
  return `${sectionIndex}:${unitKey}`;
}
